import React from 'react';
import { FormattedDate, FormattedTime } from 'react-intl';

import { mpartial, contains, identity, noop } from '../../utils';

import Checkbox from '../form/Checkbox';
import Checker from '../form/Checker';

import { ColumnType } from './constants';

import { isNil } from 'lodash'

const formatDateWithoutZone = date => {
  const pattern = /(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{1,2})/;
  return date.replace(pattern, '$3. $2. $1 $4:$5');
};

export default class Body extends React.Component {
  static defaultProps = {
    selected: [],
    onRowClick: noop,
  };

  state = {
    expandedMasterDetails: {},
  };

  static textCellRender = value => <span>{value}</span>;

  static numberCellRender = value => <span className="number">{value}</span>;

  static datetimeCellWithoutZoneRender = value => (
    <span className="datetime">
      {value ? <span>{formatDateWithoutZone(value)}</span> : ''}
    </span>
  );

  static datetimeCellRender = value => (
    <span className="datetime">
      {value ? <span><FormattedDate value={value} /> <FormattedTime value={value} /></span> : ''}
    </span>
  );

  static dateCellRender = value => (
    <span className="date">
      {value ? <FormattedDate value={value} /> : ''}
    </span>
  );

  static timeCellRender = value => (
    <span className="time">
      <FormattedTime value={value} />
    </span>
  );

  static enumCellRender = (value, column) => (
    <span className="enum">
      {value && column.enum.find(e => e.id === value) ? column.enum.find(e => e.id === value).label : ''}
    </span>
  );

  static checkCellRender = value => <Checker value={value} />;

  static yesNoRender = value => value ? 'ANO' : 'NE'

  static nullCellRender = () => <span />;

  static renderCheckbox = (value, onItemCheck, disabled) => (
    <td className="selector">
      <Checkbox value={value} onClick={(e) => e.stopPropagation()} onChange={onItemCheck} disabled={disabled} aria-label="Vybrat záznam" />
    </td>
  );

  static mapperFunction = ({ mapper = identity }) => mapper;

  static cellRender = (item, column, row, actions) => {
    const mapper = Body.mapperFunction(column);
    const value = mapper(item, row);
    if(column.maxLength) {
      const stringValue = String(value);
      if(stringValue.length > column.maxLength) {
        return stringValue.substring(0, column.maxLength) + '...';
      }
    }

    switch (column.type) {
      case ColumnType.TEXT:
        return Body.textCellRender(value);
      case ColumnType.NUMBER:
        return Body.numberCellRender(value);
      case ColumnType.ENUM:
        return Body.enumCellRender(value, column);
      case ColumnType.CHECK:
        return Body.checkCellRender(value);
      case ColumnType.YESNO:
        return Body.yesNoRender(value);
      case ColumnType.DATETIME:
        return Body.datetimeCellRender(value);
      case ColumnType.DATETIMEWITHOUTZONE:
        return Body.datetimeCellWithoutZoneRender(value);
      case ColumnType.DATE:
        return Body.dateCellRender(value);
      case ColumnType.TIME:
        return Body.timeCellRender(value);
      case ColumnType.JSX:
        if (!(column.jsx instanceof Function)) {
          console.warn('jsx property is required e.g. jsx: (row) => <span>{row.name}</span>'); // eslint-disable-line no-console
          return false;
        }
        return column.jsx(row, actions);
      default:
        return Body.nullCellRender();
    }
  };

  static cellClass = column => {
    switch (column.type) {
      case ColumnType.TEXT:
        return 'text';
      case ColumnType.NUMBER:
        return 'number';
      case ColumnType.CHECK:
        return 'check';
      case ColumnType.YESNO:
        return 'yesno';
      case ColumnType.DATETIMEWITHOUTZONE:
      case ColumnType.DATETIME:
        return 'datetime';
      case ColumnType.DATE:
        return 'date';
      case ColumnType.TIME:
        return 'time';
      case ColumnType.JSX:
        return 'jsx';
      default:
        return '';
    }
  };

  handleItemCheck = (id, value) => {
    const { selected, onSelectedChange } = this.props;
    if (value) {
      onSelectedChange([...selected, id]);
    } else {
      onSelectedChange(selected.filter(item => item !== id));
    }
  };

  renderRow = (item, i, selected) => {
    const { columns, editing, deleting, onRowClick, rowStyle, allowCheck, masterDetail, rowClass } = this.props;
    const { expandedMasterDetails } = this.state;
    const displayActionCheckbox = editing || deleting || allowCheck ? true : false;
    
    const onKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onRowClick(item);
      }
      if (e.key === ' '&& displayActionCheckbox) {
        e.preventDefault();
        this.handleItemCheck(item.id, !selected);
      }
    };

    const disableCheckboxOnRow = !isNil(item.ovmId) && !isNil(this.props.ovmId) && item.ovmId !== this.props.ovmId;
    const cssClass = rowClass ? rowClass(item) : '';

    return (
      <>
        <tr key={i} tabIndex={item.canOpen === false ? -1 : 0} role="button" aria-disabled={item.canOpen === false}
          className={cssClass}
          style={item.canOpen === false ? { cursor: 'not-allowed' } : rowStyle}
          onClick={item.canOpen === false ? undefined : () => onRowClick(item)}
          onKeyDown={onKeyDown}
        >
          {displayActionCheckbox && Body.renderCheckbox(selected, mpartial(this.handleItemCheck, item.id, !selected), disableCheckboxOnRow)}
          {columns.filter(c => c.display !== false).map((column, j) => (
            <td key={j} className={Body.cellClass(column)} onClick={column.disableOnRowClick || item.canOpen === false ? undefined : mpartial(onRowClick, item)}>
              {Body.cellRender(item[column.displayField || column.field], column, item, { toggleRowMasterDetail: this.toggleRowMasterDetail })}
            </td>
          ))}
        </ tr>
        {masterDetail && expandedMasterDetails[item.id] && masterDetail.render(item)}
      </>
    );
  };

  toggleRowMasterDetail = (id) => {
    this.setState(prevState => ({
      expandedMasterDetails: {
        ...prevState.expandedMasterDetails,
        [id]: !prevState.expandedMasterDetails[id]
      }
    }));
  }

  render() {
    const { items, selected } = this.props;

    return (
      <tbody>
        {items.map((item, i) => this.renderRow(item, i, contains(selected, item.id)))}
      </tbody>
    );
  }
}
